import React from "react";
import { Paper, Stack, Typography, Box, useTheme, Tooltip } from "@mui/material";
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

const METRICS = [
    {
        key: "no_of_test_scenarios",
        label: "Test Scenarios",
        isIncreaseGood: true,
        isFloat: false,
    },
    {
        key: "no_of_test_cases",
        label: "Test Cases",
        isIncreaseGood: true,
        isFloat: false,
    },
    {
        key: "no_of_test_rules",
        label: "Test Rules",
        isIncreaseGood: true,
        isFloat: false,
    },
    {
        key: "no_of_failed_test_scenarios",
        label: "Failed Scenarios",
        isIncreaseGood: false,
        isFloat: false,
    },
    {
        key: "average_time_in_api_calls",
        label: "Avg. API Call Time (ms)",
        isIncreaseGood: false,
        isFloat: true,
    },
];

function getComparison(curr, prev, isIncreaseGood) {
    if (prev === null || prev === undefined) {
        return {
            diff: null,
            percent: null,
            icon: null,
            color: '#A0A0A0',
            isImprovement: null,
            showBadge: false,
        };
    }
    if (curr === prev) {
        return {
            diff: 0,
            percent: 0,
            icon: null,
            color: '#A0A0A0',
            isImprovement: false,
            showBadge: true,
        };
    }
    const diff = Math.abs(curr - prev);
    const percent = prev === 0 ? null : Math.abs(((curr - prev) / prev) * 100);
    let isImprovement;
    let icon = null;
    let color = '#A0A0A0';
    if (isIncreaseGood) {
        if (curr > prev) {
            isImprovement = true;
            icon = <ArrowDropUpIcon sx={{ color: '#2CAC0C', fontSize: 18, verticalAlign: 'middle' }} />;
            color = '#2CAC0C';
        } else {
            isImprovement = false;
            icon = <ArrowDropDownIcon sx={{ color: '#CE4141', fontSize: 18, verticalAlign: 'middle' }} />;
            color = '#CE4141';
        }
    } else {
        if (curr < prev) {
            isImprovement = true;
            icon = <ArrowDropDownIcon sx={{ color: '#2CAC0C', fontSize: 18, verticalAlign: 'middle' }} />;
            color = '#2CAC0C';
        } else {
            isImprovement = false;
            icon = <ArrowDropUpIcon sx={{ color: '#CE4141', fontSize: 18, verticalAlign: 'middle' }} />;
            color = '#CE4141';
        }
    }
    return {
        diff,
        percent,
        icon,
        color,
        isImprovement,
        showBadge: true,
    };
}

const BuildStatsComparisonBox = ({ currentStats, previousStats }) => {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                mb: 3,
                backgroundColor: "var(--background-primary)!important",
                borderRadius: "4px",
                boxShadow: '0 4px 24px 0 rgba(30,30,60,0.08)',
                border: `1px solid var(--borderColor)`,
            }}
        >
            <Stack direction="row" spacing={0} useFlexGap flexWrap="wrap" justifyContent="center" alignItems="stretch">
                {METRICS.map(({ key, label, isIncreaseGood, isFloat }, idx) => {
                    const curr = currentStats[key];
                    const prev = previousStats[key];
                    const { diff, percent, icon, color, showBadge } = getComparison(curr, prev, isIncreaseGood);
                    const isLast = idx === METRICS.length - 1;
                    const tooltipTitle = (prev !== null && prev !== undefined)
                        ? `Current: ${isFloat ? Number(curr).toFixed(2) : curr} | Previous: ${isFloat ? Number(prev).toFixed(2) : prev}`
                        : '';
                    return (
                        <Paper
                            key={key}
                            elevation={0}
                            sx={{
                                minWidth: 150,
                                flex: '1 1 150px',
                                p: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                bgcolor: 'transparent',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRight: isLast ? 'none' : `1px solid var(--borderColor)`,
                                borderRadius: 0,
                                boxShadow: 'none',
                            }}
                        >
                            <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--text-primary)', mb: 0.5 }}>
                                {isFloat ? Number(curr).toFixed(2) : curr}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'var(--text-secondary)', mb: 0, fontWeight: 500, letterSpacing: 0.2 }}>
                                {label}
                            </Typography>
                            {showBadge && (
                                <Tooltip title={<span className="text-sm">{tooltipTitle}</span>} arrow disableInteractive>
                                    <Box
                                        sx={{
                                            mt: 1,
                                            px: 1.5,
                                            py: 0.5,
                                            borderRadius: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            bgcolor: color + '22', // faded color
                                            color: color,
                                            fontWeight: 500,
                                            fontSize: 14,
                                            minHeight: 32,
                                            minWidth: 90,
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {icon}
                                        {diff !== null && diff !== 0 && (
                                            <>
                                                <Typography variant="body2" sx={{ color: color, fontWeight: 600, mr: 0.5 }}>
                                                    {isFloat ? Number(diff).toFixed(2) : diff}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: color, fontWeight: 600, mr: 0.5 }}>
                                                    ({percent !== null ? percent.toFixed(1) + '%' : ''})
                                                </Typography>
                                            </>
                                        )}
                                        {diff === 0 && (
                                            <Typography variant="body2" sx={{ color: '#A0A0A0', fontWeight: 500, mr: 0.5 }}>
                                                0 (0.0%)
                                            </Typography>
                                        )}
                                    </Box>
                                </Tooltip>
                            )}
                        </Paper>
                    );
                })}
            </Stack>
        </Paper>
    );
};

export default BuildStatsComparisonBox; 